/**
 * InMobi Choice. Consent Manager Tag v3.0 (for TCF 2.2)
 *
 * Keep the vendor-provided CMP bootstrap IIFE here exactly as currently
 * supplied by InMobi. It must execute early so __tcfapi is available before
 * consent-dependent vendors run.
 */

(function () {
  var host = window.location.hostname;
  var element = document.createElement('script');
  var firstScript = document.getElementsByTagName('script')[0];
  var url = 'https://cmp.inmobi.com'.concat(
    '/choice/',
    'j6ef-UjNU-sjS',
    '/',
    host,
    '/choice.js?tag_version=V3',
  );
  var uspTries = 0;
  var uspTriesLimit = 3;

  element.async = true;
  element.type = 'text/javascript';
  element.src = url;

  firstScript.parentNode.insertBefore(element, firstScript);

  function makeStub() {
    var TCF_LOCATOR_NAME = '__tcfapiLocator';
    var queue = [];
    var win = window;
    var cmpFrame;

    function addFrame() {
      var doc = win.document;
      var otherCMP = !!win.frames[TCF_LOCATOR_NAME];

      if (!otherCMP) {
        if (doc.body) {
          var iframe = doc.createElement('iframe');

          iframe.style.cssText = 'display:none';
          iframe.name = TCF_LOCATOR_NAME;
          doc.body.appendChild(iframe);
        } else {
          setTimeout(addFrame, 5);
        }
      }

      return !otherCMP;
    }

    function tcfAPIHandler() {
      var gdprApplies;
      var args = arguments;

      if (!args.length) {
        return queue;
      }

      if (args[0] === 'setGdprApplies') {
        if (args.length > 3 && args[2] === 2 && typeof args[3] === 'boolean') {
          gdprApplies = args[3];

          if (typeof args[2] === 'function') {
            args[2]('set', true);
          }
        }

        return;
      }

      if (args[0] === 'ping') {
        var retr = {
          gdprApplies: gdprApplies,
          cmpLoaded: false,
          cmpStatus: 'stub',
        };

        if (typeof args[2] === 'function') {
          args[2](retr);
        }

        return;
      }

      if (args[0] === 'init' && typeof args[3] === 'object') {
        args[3] = Object.assign(args[3], { tag_version: 'V3' });
      }

      queue.push(args);
    }

    function postMessageEventHandler(event) {
      var msgIsString = typeof event.data === 'string';
      var json = {};

      try {
        json = msgIsString ? JSON.parse(event.data) : event.data;
      } catch (ignore) {
        return;
      }

      var payload = json.__tcfapiCall;

      if (!payload) {
        return;
      }

      window.__tcfapi(
        payload.command,
        payload.version,
        function (retValue, success) {
          var returnMsg = {
            __tcfapiReturn: {
              returnValue: retValue,
              success: success,
              callId: payload.callId,
            },
          };

          if (msgIsString) {
            returnMsg = JSON.stringify(returnMsg);
          }

          if (event && event.source && event.source.postMessage) {
            event.source.postMessage(returnMsg, '*');
          }
        },
        payload.parameter,
      );
    }

    while (win) {
      try {
        if (win.frames[TCF_LOCATOR_NAME]) {
          cmpFrame = win;
          break;
        }
      } catch (ignore) {
        // Cross-origin parent access may throw; continue current behavior.
      }

      if (win === window.top) {
        break;
      }

      win = win.parent;
    }

    if (!cmpFrame) {
      addFrame();
      win.__tcfapi = tcfAPIHandler;
      win.addEventListener('message', postMessageEventHandler, false);
    }
  }

  makeStub();

  var uspStubFunction = function () {
    var args = arguments;

    if (typeof window.__uspapi !== uspStubFunction) {
      setTimeout(function () {
        if (typeof window.__uspapi !== 'undefined') {
          window.__uspapi.apply(window.__uspapi, args);
        }
      }, 500);
    }
  };

  var checkIfUspIsReady = function () {
    uspTries++;

    if (window.__uspapi === uspStubFunction && uspTries < uspTriesLimit) {
      // Keep this warning if required by the vendor integration.
      console.warn('USP is not accessible');
    } else {
      clearInterval(uspInterval);
    }
  };

  if (typeof window.__uspapi === 'undefined') {
    window.__uspapi = uspStubFunction;
    var uspInterval = setInterval(checkIfUspIsReady, 6000);
  }
})();

/**
 * Application-owned consent bridge.
 *
 * This preserves the existing implementation's observable behavior:
 * - process `tcloaded` and `useractioncomplete`;
 * - use non-IAB vendor 13 for GDPR analytics consent;
 * - wait 1.5 seconds before updating Adobe Launch in the GDPR path;
 * - enable/disable dtrum as before;
 * - dispatch `event-view-end` whenever a qualifying consent callback resolves
 *   and digitalDataLayer becomes available.
 */

const ANALYTICS_NON_IAB_VENDOR_ID = 13;
const ADOBE_INITIALIZATION_DELAY_MS = 1500;
const GLOBAL_POLL_INTERVAL_MS = 50;

const waitForDomReady = (callback) => {
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', callback, { once: true });
    return;
  }

  callback();
};

/**
 * Deliberately has no timeout to preserve existing behavior: the original
 * implementation waited indefinitely for digitalDataLayer.
 */
const waitForDigitalDataLayer = () =>
  new Promise((resolve) => {
    if (window.digitalDataLayer) {
      resolve(window.digitalDataLayer);
      return;
    }

    const intervalId = window.setInterval(() => {
      if (window.digitalDataLayer) {
        window.clearInterval(intervalId);
        resolve(window.digitalDataLayer);
      }
    }, GLOBAL_POLL_INTERVAL_MS);
  });

/**
 * Deliberately has no timeout to preserve the existing GDPR behavior, which
 * waited until Adobe Launch became available.
 */
const waitForSatellite = () =>
  new Promise((resolve) => {
    if (window._satellite) {
      resolve(window._satellite);
      return;
    }

    const intervalId = window.setInterval(() => {
      if (window._satellite) {
        window.clearInterval(intervalId);
        resolve(window._satellite);
      }
    }, 300);
  });

const dispatchViewEnd = async () => {
  const dataLayer = await waitForDigitalDataLayer();

  if (!dataLayer || !document.body) {
    return;
  }

  document.body.dispatchEvent(new CustomEvent('event-view-end'));
};

const setDtrumConsent = (isEnabled) => {
  if (!window.dtrum) {
    return;
  }

  if (isEnabled) {
    window.dtrum.enable?.();
    return;
  }

  window.dtrum.disable?.();
};

const setAdobeAnalyticsConsent = (analyticsConsent) => {
  if (!window._satellite?.setVar) {
    return false;
  }

  window._satellite.setVar('CC-Analyticsconsent', analyticsConsent);
  return true;
};

const getNonIabVendorConsents = () =>
  new Promise((resolve) => {
    window.__tcfapi(
      'getNonIABVendorConsents',
      2,
      (nonIabConsent, nonIabSuccess) => {
        resolve({ nonIabConsent, nonIabSuccess });
      },
    );
  });

const getAnalyticsConsentFromNonIabResponse = (
  nonIabConsent,
  nonIabSuccess,
) => {
  if (!nonIabSuccess || !nonIabConsent?.nonIabVendorConsents) {
    return false;
  }

  return Boolean(
    nonIabConsent.nonIabVendorConsents[ANALYTICS_NON_IAB_VENDOR_ID],
  );
};

const applyNonGdprConsent = async () => {
  // Existing behavior: non-GDPR users are granted analytics consent.
  const analyticsConsent = true;

  // Previously this assumed _satellite was already defined. Waiting avoids an
  // intermittent runtime error without changing the final intended outcome.
  await waitForSatellite();
  setAdobeAnalyticsConsent(analyticsConsent);
  setDtrumConsent(analyticsConsent);
  await dispatchViewEnd();
};

const applyGdprConsent = async () => {
  const { nonIabConsent, nonIabSuccess } = await getNonIabVendorConsents();

  const analyticsConsent = getAnalyticsConsentFromNonIabResponse(
    nonIabConsent,
    nonIabSuccess,
  );

  await waitForSatellite();

  // Retained from current implementation: allows AppMeasurement/Launch time
  // to initialize before setting its consent variable.
  await new Promise((resolve) => {
    window.setTimeout(resolve, ADOBE_INITIALIZATION_DELAY_MS);
  });

  setAdobeAnalyticsConsent(analyticsConsent);

  /*
   * Preserved existing behavior:
   * DYconsent was initialized to false and never changed, so GDPR requests
   * always disabled dtrum regardless of analyticsConsent.
   *
   * Do not change this to `setDtrumConsent(analyticsConsent)` without
   * analytics/privacy owner approval; that would be a behavior change.
   */
  setDtrumConsent(false);

  await dispatchViewEnd();
};

const isRelevantConsentEvent = (tcData, listenerSuccess) =>
  Boolean(
    listenerSuccess &&
    (tcData?.eventStatus === 'tcloaded' ||
      tcData?.eventStatus === 'useractioncomplete'),
  );

/**
 * Registers the TCF listener.
 *
 * The exported name can remain `reportWebVitals` temporarily to avoid changes
 * to existing imports. Rename call sites later to
 * `initializeConsentDrivenAnalytics` when convenient.
 */
const reportWebVitals = () => {
  waitForDomReady(() => {
    if (typeof window.__tcfapi !== 'function') {
      return;
    }

    window.__tcfapi('addEventListener', 2, async (tcData, listenerSuccess) => {
      if (!isRelevantConsentEvent(tcData, listenerSuccess)) {
        return;
      }

      if (!tcData.gdprApplies) {
        await applyNonGdprConsent();
        return;
      }

      await applyGdprConsent();
    });
  });
};

export default reportWebVitals;
